import { useMutation } from '@apollo/client/react';
import { CardPropertyDetailsFragmentDoc } from '@graphql/fragments/cards/property.graphql';
import { SetCardPropertyDocument } from '@graphql/mutations/cards/property.graphql';
import { useCardAttributes } from '@hooks/use-card-attributes';
import { logger } from '@modules/logger';
import { cardClaimContext } from 'app-next/claim/card/contexts';
import { useCallback, useContext } from 'react';

import type { ValueProvider } from '.cache/__types__';

export type AttributeName = 'layout' | 'title' | 'color'; // these should probably be defined elsewhere at some point, but putting here for now

interface SetCardPropertyProps {
  cardId: string;
  propertyId?: string | null;
  value?: string;
  valueLocked?: boolean;
  attributeName: AttributeName;
  valueProvider?: ValueProvider;
}

/**
 * A hook that wraps the create and update card property mutations
 * Used as a utility so the caller doesn't need to worry about which mutation to use
 * @param cardId the id of the card to mutate
 * @param propertyId the id of the property to mutate, if null or undefined, will create a new property
 * @param value the value to set the property to
 * @returns an object with a mutate() function usable for setting the title of a card
 */
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function useSetCardProperty() {
  const { cardAttributeTypes } = useCardAttributes();
  const [set, setResponse] = useMutation(SetCardPropertyDocument);

  const { claimCardToken } = useContext(cardClaimContext);

  /* 
    If we're running mutations during the card claim flow,
    then we need to override the authorization headers to use the
    Claim Card Token.
  */
  const mutationContext = claimCardToken
    ? {
        headers: {
          authorization: `ClaimCard ${claimCardToken}`,
        },
      }
    : undefined;

  // biome-ignore lint/correctness/useExhaustiveDependencies: historic
  const mutate = useCallback(
    ({ cardId, propertyId, value, valueLocked, attributeName, valueProvider }: SetCardPropertyProps) => {
      const attribute = cardAttributeTypes?.find((attr) => attr.name === attributeName);
      if (attribute?.id) {
        return set({
          context: mutationContext,
          variables: {
            input: {
              card_id: cardId,
              attribute_type_id: attribute.id,
              value,
              value_locked: valueLocked,
              value_provider: valueProvider,
            },
          },
          optimisticResponse: {
            setCardProperty: {
              property: {
                __typename: 'CardProperty',
                id: propertyId ?? '',
                card_id: cardId,
                value,
                value_locked: valueLocked ?? false,
                attribute_type: attribute,
                template: null,
                template_id: null,
                is_optional: null,
                instruction_text: null,
                value_provider: valueProvider ?? null,
                stage: null,
              },
            },
          },
          /* 
            Note: 'update' gets called twice. Once with the optimisticResponse (above)
            then again with the actual network response
          */
          update: (cache, { data }) => {
            const responseProperty = data?.setCardProperty;
            if (!responseProperty || !cardId) return;

            const newCardPropertyRef = cache.writeFragment({
              // We must prefix this id with the string 'CardProperty:' otherwise the resulting cache ref
              // won't actually point to the right place
              id: `CardProperty:${data.setCardProperty?.property?.id}`,
              data: data.setCardProperty?.property,
              fragment: CardPropertyDetailsFragmentDoc,
              fragmentName: 'cardPropertyDetails',
            });

            cache.modify({
              id: cache.identify({ __typename: 'Card', id: cardId }),
              fields: {
                properties(existingProperties) {
                  return {
                    ...existingProperties,
                    [attributeName]: newCardPropertyRef,
                  };
                },
              },
            });
          },
        });
      } else {
        logger.error('No attribute found', { cardId, attributeName });
      }
    },
    [set, cardAttributeTypes],
  );

  return {
    mutate,
    data: setResponse,
    loading: setResponse.loading,
  };
}
